import { html, PolymerElement } from '@polymer/polymer/polymer-element.js';
import { KatapultWidget } from './katapult-widget';
import { DateTime } from 'luxon';
import { SquashNulls } from '../../..//modules/SquashNulls.js';
import '../../katapult-dialog-legacy/katapult-dialog-legacy';
import '../../katapult-elements/katapult-button.js';

/**
 * Represents an immutable 2-element vector and provides useful methods.
 * (helper for MKPBallJar)
 */
class V {
  /**
   * Creates a new vector.
   * @param {number} x x coordinate
   * @param {number} y y coordinate
   */
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }

  /**
   * Returns the midpoint of two vectors.
   * @param {V} a a vector
   * @param {V} b a vector
   * @returns {Vector} their midpoint
   */
  static midpoint(a, b) {
    return new V((a.x + b.x) / 2, (a.y + b.y) / 2);
  }

  /**
   * The magnitude of this vector.
   */
  get mag() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  toString() {
    return `V(${this.x}, ${this.y})`;
  }

  /**
   * Adds another vector to this one.
   * @param {V} that another vector
   * @returns {V} the sum
   */
  add(that) {
    return new V(this.x + that.x, this.y + that.y);
  }

  /**
   * Subtracts another vector from this one.
   * @param {V} that another vector
   * @returns {V} the difference
   */
  sub(that) {
    return new V(this.x - that.x, this.y - that.y);
  }

  /**
   * Multiplies this vector by a scalar.
   * @param {number} val the scalar value
   * @returns {V} the scaled vector
   */
  smul(val) {
    return new V(val * this.x, val * this.y);
  }

  /**
   * Calculates the dot product of this vector and another.
   * @param {V} that another vector
   * @returns {number} the dot product
   */
  dot(that) {
    return this.x * that.x + this.y * that.y;
  }
}

/**
 * Represents a ball in the jar simulation.
 * (helper for MKPBallJar)
 */
class B {
  /**
   * Creates a new ball.
   * @param {V} pos the position of the ball
   * @param {number} r the radius of the ball
   * @param {V} vel initial velocity
   */
  constructor(pos, r, vel = new V(0, 0)) {
    this.pos = this._pos = pos;
    this.r = r;
    this.vel = vel;
  }

  /**
   * Draws this ball to a canvas context.
   * @param {CanvasRenderingContext2D} ctx a 2D rendering context
   * @param {string} color the color of the ball
   */
  draw(ctx, color) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(this.pos.x, this.pos.y, this.r - 1, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = 'black';
  }

  /**
   * Resolves 2D collisions between two balls.
   * @param {B} b1 a ball
   * @param {B} b2 a ball
   */
  static resolveBallCollision(b1, b2) {
    // the percentage of energy to keep after collision
    const energyLossMult = 0.92;

    // short-circuit if they are not colliding
    if (b1.pos.sub(b2.pos).mag > b1.r + b2.r) return;

    // formula from wikipedia https://en.wikipedia.org/wiki/Elastic_collision
    const newV1 = b1.vel.sub(
      b1.pos.sub(b2.pos).smul(b1.vel.sub(b2.vel).dot(b1.pos.sub(b2.pos)) / (b1.pos.sub(b2.pos).mag * b1.pos.sub(b2.pos).mag))
    );

    const newV2 = b2.vel.sub(
      b2.pos.sub(b1.pos).smul(b2.vel.sub(b1.vel).dot(b2.pos.sub(b1.pos)) / (b2.pos.sub(b1.pos).mag * b2.pos.sub(b1.pos).mag))
    );

    // separate balls so they don't stick
    const mid = V.midpoint(b1.pos, b2.pos);
    b1.pos = mid.add(b1.pos.sub(mid).smul(b1.r / b1.pos.sub(mid).mag));
    b2.pos = mid.add(b2.pos.sub(mid).smul(b2.r / b2.pos.sub(mid).mag));

    // scale down velocities for stabilization
    b1.vel = newV1.smul(energyLossMult);
    b2.vel = newV2.smul(energyLossMult);
  }

  /**
   * Processes this ball's movement.
   * @param {V} g gravity vector
   * @param {number} dt the amount of time since the last call
   * @param {number[]} walls positions of left wall, right wall, and floor
   */
  process(g, dt, walls) {
    // save old position
    this._pos = this.pos;

    // update velocity and position
    this.vel = this.vel.add(g.smul(dt));
    this.pos = this.pos.add(this.vel.smul(dt));

    // check for collisions
    this._resolveWallCollisions(...walls);
  }

  /**
   * Handles wall collisions for this ball.
   * @param {number} left left wall x-coordinate
   * @param {number} right right wall x-coordinate
   * @param {number} floor floor y-coordinate
   */
  _resolveWallCollisions(left, right, floor) {
    // percentages of energy to keep for collisions and rolling
    const energyLossMult = 0.85;
    const rollingFrictionMult = 0.999;

    // if touching the left wall and moving left
    if (this.pos.x <= left + this.r && this.vel.x < 0) {
      this.pos.x = left + this.r;
      this.vel.x *= -energyLossMult;

      // if touching the right wall and moving right
    } else if (this.pos.x >= right - this.r && this.vel.x > 0) {
      this.pos.x = right - this.r;
      this.vel.x *= -energyLossMult;
    }

    // if touching the floor and moving down
    if (this.pos.y >= floor - this.r && this.vel.y > 0) {
      this.pos.y = floor - this.r;
      this.vel.y *= -energyLossMult;
    }

    // if touching the floor and moving sideways
    if (this.pos.y >= floor - this.r - 1 && this.vel.x != 0) {
      this.vel.x *= rollingFrictionMult;
    }
  }
}

/**
 * MKPBallJar is a helper element for the kpi-widget that is essentially a
 * canvas with built-in behavior.
 *
 * It creates an interactive "jar" of bouncing balls to represent the amount of
 * MKP a user has earned.
 */
class MKPBallJar extends PolymerElement {
  constructor() {
    super();

    // canvas size is hard-coded to a high-dpi value
    this.C_WIDTH = 800;
    this.C_HEIGHT = 600;

    // constants for positioning and sizing
    this.JAR_BORDER = this.C_WIDTH * 0.75;
    this.JAR_PADDING = 20;

    this.BALL_RADIUS = 25;
  }

  static get is() {
    return 'mkp-ball-jar';
  }

  static get template() {
    return html`
      <style>
        #canvas {
          width: 400px;
          height: 300px;
        }
      </style>
      <canvas id="canvas" width="800" height="600"></canvas>
    `;
  }

  static get properties() {
    return {
      kp: {
        type: Number,
        value: 0,
        observer: '_update'
      },
      team: {
        type: Boolean,
        value: false
      },
      target: {
        type: Number
      },
      pacer: {
        type: Number
      },
      loaded: {
        type: Boolean
      }
    };
  }

  ready() {
    super.ready();

    // skip loading process if initialized with kp value
    if (this.kp) this.loaded = true;

    if (this.team) this.BALL_RADIUS /= 2;

    // empty list of balls
    this.balls = [];

    // used to prevent multiple adjacent updates from doubling up the spawning
    this.filling = false;

    // internal timekeeping
    this.t = 0;

    // start the async animation loop
    this.animate();

    // start filling if initialized with kp value
    if (this.kp) this._update(this.kp);
  }

  async animate() {
    let foundCanvas = false;

    while (true) {
      // attach click handler if this is the first time the canvas is accessible
      if (!foundCanvas && this.canvas) {
        this.canvas.addEventListener('mousedown', (e) => this.clickHandler(e, this));
        foundCanvas = true;
      }

      // try processing and rendering the scene for this frame
      try {
        this._process();
        this._render();
      } catch (e) {
        console.log(e);
      }

      // increment internal timer
      this.t += 0.01;

      // sleep 10ms between frames
      await new Promise((r) => setTimeout(r, 10));
    }
  }

  /**
   * Draws text centered at (x, y)
   * @param {string} text text to render
   * @param {number} x x position
   * @param {number} y y position
   * @param {string} color color
   */
  centeredText(text, x, y, color = 'black') {
    this.ctx.fillStyle = color;

    let t = this.ctx.measureText(text);
    this.ctx.fillText(text, Math.max(x - t.width / 2, 0), y);

    this.ctx.fillStyle = 'black';
  }

  /**
   * Process the scene.
   */
  _process() {
    // handle collisions between every unique pair of balls
    for (let i = 0; i < this.balls.length - 1; i++) {
      for (let j = i + 1; j < this.balls.length; j++) {
        B.resolveBallCollision(this.balls[i], this.balls[j]);
      }
    }

    // helper function for getting the distance from a ball to the nearest wall
    const distToWall = (ball) =>
      Math.max(
        ball.pos.x - this.JAR_PADDING,
        this.JAR_BORDER - this.JAR_PADDING - ball.pos.x,
        this.C_HEIGHT - this.JAR_PADDING - ball.pos.y
      );

    // update positions of all balls, including boundary collisions
    // sort balls by distance to wall to reduce jitter
    for (const b of this.balls.slice().sort((a, b) => distToWall(b) - distToWall(a))) {
      b.process(
        new V(0, 1000), // velocity vector
        0.01, // time step between frames
        [
          this.JAR_PADDING, // left wall pos
          this.JAR_BORDER - this.JAR_PADDING, // right wall pos
          this.C_HEIGHT - this.JAR_PADDING // floor pos
        ]
      );
    }
  }

  /**
   * Handles mouse clicks. Adds "impulse"-like effect centered at mouse click,
   * disturbing the scene.
   */
  clickHandler(e, obj) {
    const clickPos = new V(e.offsetX * 2, e.offsetY * 2);
    for (const b of obj.balls) {
      if (b.pos.sub(clickPos).mag < 5 * b.r) {
        const v = b.pos.sub(clickPos);
        b.vel = b.vel.add(v.smul(600 / v.mag));
      }
    }
  }

  /**
   * Render the scene.
   */
  _render() {
    const ballColor = this.team ? '#FF8300' : '#007299';

    // clear canvas
    this.ctx.clearRect(0, 0, this.C_WIDTH, this.C_HEIGHT);

    if (!this.loaded) {
      // draw the "jar"
      this.ctx.fillStyle = 'black';
      this.ctx.beginPath();
      this.ctx.moveTo(this.JAR_PADDING - 2, this.JAR_PADDING);
      this.ctx.lineTo(this.JAR_PADDING - 2, this.C_HEIGHT - this.JAR_PADDING + 2);
      this.ctx.lineTo(this.JAR_BORDER - this.JAR_PADDING + 2, this.C_HEIGHT - this.JAR_PADDING + 2);
      this.ctx.lineTo(this.JAR_BORDER - this.JAR_PADDING + 2, this.JAR_PADDING);
      this.ctx.stroke();

      this.centeredText('Press the load button in the corner to start,', this.JAR_BORDER / 2, this.C_HEIGHT / 2 - 10, ballColor);
      this.centeredText('or the dropdown to learn about the widget.', this.JAR_BORDER / 2, this.C_HEIGHT / 2 + 30, ballColor);
    } else {
      // draw target line
      this.ctx.beginPath();
      this.ctx.moveTo(this.JAR_PADDING, this.C_HEIGHT - this.JAR_PADDING - this.FILL_HEIGHT);
      this.ctx.lineTo(this.JAR_BORDER - this.JAR_PADDING, this.C_HEIGHT - this.JAR_PADDING - this.FILL_HEIGHT);
      this.ctx.setLineDash([10, 5]);
      this.ctx.stroke();
      this.ctx.setLineDash([]);

      // draw the pacer area
      this.ctx.fillStyle = 'lightgrey';
      this.ctx.fillRect(
        this.JAR_PADDING,
        this.C_HEIGHT - this.JAR_PADDING - this.FILL_HEIGHT * this.pacer + 5,
        this.JAR_BORDER - 2 * this.JAR_PADDING,
        this.FILL_HEIGHT * this.pacer - 5
      );

      try {
        // draw gradient at the top of the pacer area
        let gr = this.ctx.createLinearGradient(
          0,
          this.C_HEIGHT - this.JAR_PADDING - this.FILL_HEIGHT * this.pacer - 15,
          0,
          this.C_HEIGHT - this.JAR_PADDING - this.FILL_HEIGHT * this.pacer + 15
        );
        gr.addColorStop(0, 'white');
        gr.addColorStop(1, 'lightgrey');
        this.ctx.fillStyle = gr;
        this.ctx.fillRect(
          this.JAR_PADDING,
          this.C_HEIGHT - this.JAR_PADDING - this.FILL_HEIGHT * this.pacer - 15,
          this.JAR_BORDER - 2 * this.JAR_PADDING,
          30
        );
      } catch (e) {
        // throws error before data has been populated
      }

      // draw the balls
      for (const b of this.balls) {
        b.draw(this.ctx, ballColor);
      }

      // draw the "jar"
      this.ctx.fillStyle = 'black';
      this.ctx.beginPath();
      this.ctx.moveTo(this.JAR_PADDING - 2, this.JAR_PADDING);
      this.ctx.lineTo(this.JAR_PADDING - 2, this.C_HEIGHT - this.JAR_PADDING + 2);
      this.ctx.lineTo(this.JAR_BORDER - this.JAR_PADDING + 2, this.C_HEIGHT - this.JAR_PADDING + 2);
      this.ctx.lineTo(this.JAR_BORDER - this.JAR_PADDING + 2, this.JAR_PADDING);
      this.ctx.stroke();

      // draw the KP counter and progress to next drop
      this.ctx.fillStyle = 'black';
      this.ctx.font = '28px Roboto';
      this.ctx.fillText('MKP', this.JAR_BORDER, this.JAR_PADDING + 30);
      this.ctx.fillText('Weekly Avg', this.JAR_BORDER, this.JAR_PADDING + 180);
      this.ctx.fillText('End of Day Avg', this.JAR_BORDER, this.JAR_PADDING + 330);

      this.ctx.fillStyle = ballColor;
      this.ctx.font = '80px Roboto';

      if (this.filling) {
        this.ctx.fillText(`${this.balls.length}`, this.JAR_BORDER, 120);
      } else {
        this.ctx.fillText(`${this.kp.toFixed(1)}`, this.JAR_BORDER, 120);
        this.ctx.fillStyle = 'lightgrey';
        this.ctx.fillRect(this.JAR_BORDER, 130, 160, 8);
        this.ctx.fillStyle = ballColor;
        this.ctx.fillRect(this.JAR_BORDER, 130, 160 * (this.kp - Math.floor(this.kp)), 8);
      }

      if (this.target) this.ctx.fillText(`${this.target.toFixed(1)}`, this.JAR_BORDER, 270);

      this.ctx.fillStyle = 'grey';
      if (this.target && this.pacer) {
        const paceVal = this.pacer * this.target;
        this.ctx.fillText(`${paceVal.toFixed(1)}`, this.JAR_BORDER, 420);
      }
    }
  }

  /**
   * Handle change to KP amount. Periodically spawn balls at the top of the
   * jar until new KP value is reached.
   * @param {number} kp new KP value
   */
  async _update(kp) {
    // height of "full" jar based on ball volume and circle 2D packing density
    this.FILL_HEIGHT = (Math.PI * this.BALL_RADIUS * this.BALL_RADIUS * this.target) / (0.9 * (this.JAR_BORDER - 2 * this.JAR_PADDING));

    // sets canvas vars, since _update is called when the kp property is first initialized as well.
    if (!this.ctx) {
      this.canvas = this.shadowRoot.getElementById('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.ctx.font = '28px Roboto';
      this.ctx.lineWidth = 4;
    }

    if (!kp) kp = 0;

    // short circuit if the ball list has not been created yet
    if (!this.balls) return;

    // if not already filling up
    if (!this.filling) {
      this.filling = true;
      while (this.balls.length < Math.floor(kp)) {
        this.balls.push(
          new B(
            new V(Math.random() * (this.JAR_BORDER - 4 * this.JAR_PADDING) + 2 * this.JAR_PADDING, this.JAR_PADDING),
            this.BALL_RADIUS,
            new V(Math.random() * 400 - 200, 20)
          )
        );

        // wait 250ms between spawns for individual, 40ms for team
        await new Promise((r) => setTimeout(r, this.team ? 40 : 250));
      }
      this.filling = false;
    }
  }
}
window.customElements.define('mkp-ball-jar', MKPBallJar);

/**
 * MKPChart is a helper element for the kpi-widget that is essentially a
 * canvas with built-in behavior.
 *
 * It creates a progress-bar type chart that provides an at-a-glance
 * productivity view.
 */
class MKPChart extends PolymerElement {
  constructor() {
    super();

    // canvas size is hard coded to a high-dpi value
    this.C_WIDTH = 1200;
    this.C_HEIGHT = 400;

    // positioning and sizing constants
    this.BAR_X_OFF = 20;
    this.BAR_Y_OFF = 80;
    this.BAR_WIDTH = 900 - 2 * this.BAR_X_OFF;
    this.BAR_HEIGHT = 400 - 2 * this.BAR_Y_OFF;
  }

  static get is() {
    return 'mkp-chart';
  }

  static get template() {
    return html`
      <style>
        #canvas {
          width: 600px;
          height: 200px;
        }
      </style>
      <canvas id="canvas" width="1200" height="400"></canvas>
    `;
  }

  static get properties() {
    return {
      kp: {
        type: Number,
        value: 0,
        observer: '_update'
      },
      team: {
        type: Boolean,
        value: false
      },
      target: {
        type: Number
      },
      pacer: {
        type: Number
      },
      loaded: {
        type: Boolean
      }
    };
  }

  ready() {
    super.ready();

    // progressLength keeps track of the current length
    // of the bar (whether it is tweening or not)
    this.progressLength = 0;

    // internal time keeper
    this.t = 0;

    // start async animation loop
    this.animate();
  }

  async animate() {
    while (true) {
      // try rendering the bar
      try {
        this.renderBar();
      } catch (e) {
        console.log(e);
      }

      // increment time keeper
      this.t += 0.02;

      // sleep 20ms between frames
      await new Promise((r) => setTimeout(r, 20));
    }
  }

  /**
   * Draws a line from (x1, y1) to (x2, y2).
   * @param {number} x1 x position of start point
   * @param {number} y1 y position of start point
   * @param {number} x2 x position of end point
   * @param {number} y2 y position of end point
   * @param {number[]} dash dash setting for setLineDash
   * @param {string} color line color
   * @param {number} width line width
   */
  line(x1, y1, x2, y2, dash = [], color = 'black', width = 4) {
    this.ctx.lineWidth = width;
    this.ctx.strokeStyle = color;
    this.ctx.setLineDash(dash);
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
    this.ctx.strokeStyle = 'black';
    this.ctx.lineWidth = 4;
  }

  /**
   * Draws text centered at (x, y)
   * @param {string} text text to render
   * @param {number} x x position
   * @param {number} y y position
   * @param {string} color color
   */
  centeredText(text, x, y, color = 'black') {
    this.ctx.fillStyle = color;

    let t = this.ctx.measureText(text);
    this.ctx.fillText(text, Math.max(x - t.width / 2, 0), y);

    this.ctx.fillStyle = 'black';
  }

  /**
   * Fills a rectangle with diagonal lines. Can use the canvas' t value
   * to animate a pulsing effect.
   * @param {number} x x position of top left corner
   * @param {number} y y position of top left corner
   * @param {number} w rectangle width
   * @param {number} h rectangle width
   * @param {number} s spacing between fill lines
   * @param {string} color color of fill lines
   * @param {number} offset where to start the first fill line
   * @param {number} width width of fill lines
   * @param {boolean} pulse whether or not to apply the pulse effect
   */
  shadeRect(x, y, w, h, s, color, offset = 0, width = 4, pulse = false) {
    for (let i = 0; i * s + offset < w + h; i++) {
      const d = i * s + offset;
      this.line(
        x + (d > h ? d - h : 0),
        y + (d > h ? h : d),
        x + (d > w ? w : d),
        y + (d > w ? d - w : 0),
        [],
        color,
        pulse ? width + (width - 3) * Math.sin(2 * this.t - i / 10) : width // oscillating ripple effect
      );
    }
  }

  /**
   * Renders the bar to the canvas.
   */
  renderBar() {
    // KP width of entire bar (125% of the larger of the target and the currtent value)
    const maxKp = Math.max(this.target, this.kp) * 1.25;

    // pixel width of target length
    const targetLength = (this.BAR_WIDTH * this.target) / maxKp;

    const barColor = this.team ? '#FF8300' : '#007299';

    this.ctx.font = '28px Roboto';
    this.ctx.clearRect(0, 0, this.C_WIDTH, this.C_HEIGHT);

    // shade pacer rect
    this.ctx.fillStyle = 'lightgrey';
    this.ctx.fillRect(this.BAR_X_OFF, this.BAR_Y_OFF, targetLength * this.pacer, this.BAR_HEIGHT);
    this.ctx.fillStyle = 'black';

    // shade progress rect
    this.shadeRect(this.BAR_X_OFF, this.BAR_Y_OFF, this.progressLength, this.BAR_HEIGHT, 12, barColor, 0, 5, true);

    // draw outline
    this.ctx.strokeRect(this.BAR_X_OFF, this.BAR_Y_OFF, this.BAR_WIDTH, this.BAR_HEIGHT);

    // draw load message if unloaded
    if (!this.loaded) {
      this.centeredText(
        'Press the load button in the corner to start,',
        this.BAR_X_OFF + this.BAR_WIDTH / 2,
        this.BAR_Y_OFF + this.BAR_HEIGHT / 2 - 10,
        barColor
      );
      this.centeredText(
        'or the dropdown to learn about the widget.',
        this.BAR_X_OFF + this.BAR_WIDTH / 2,
        this.BAR_Y_OFF + this.BAR_HEIGHT / 2 + 30,
        barColor
      );
    } else {
      // draw line down at progress
      this.line(
        this.BAR_X_OFF + this.progressLength,
        this.BAR_Y_OFF,
        this.BAR_X_OFF + this.progressLength,
        this.BAR_Y_OFF + this.BAR_HEIGHT + 12
      );

      // draw line down at pace
      this.line(
        this.BAR_X_OFF + targetLength * this.pacer,
        this.BAR_Y_OFF,
        this.BAR_X_OFF + targetLength * this.pacer,
        this.BAR_Y_OFF + this.BAR_HEIGHT + 12
      );

      // draw progress label
      const tweenKp = maxKp * (this.progressLength / this.BAR_WIDTH);
      if (tweenKp)
        this.centeredText(`${tweenKp.toFixed(1)}`, this.BAR_X_OFF + this.progressLength, this.BAR_Y_OFF + this.BAR_HEIGHT + 40, barColor);

      // draw pacer label
      this.centeredText(
        `${(this.target * this.pacer).toFixed(1)}`,
        this.BAR_X_OFF + targetLength * this.pacer,
        this.BAR_Y_OFF + this.BAR_HEIGHT + 68,
        'grey'
      );

      // draw dashed target line
      this.line(
        this.BAR_X_OFF + targetLength,
        this.BAR_Y_OFF,
        this.BAR_X_OFF + targetLength,
        this.BAR_Y_OFF + this.BAR_HEIGHT + 12,
        [10, 5]
      );

      // draw weekday labels
      const weekdayLabelWidth = targetLength / 5;
      const days = ['Fri', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      let rectStart = this.BAR_X_OFF;
      for (let i = 0; i < days.length; i++) {
        let rectWidth = [0, 5].includes(i) ? weekdayLabelWidth / 2 : weekdayLabelWidth;
        this.centeredText(days[i], rectStart + rectWidth / 2, this.BAR_Y_OFF - 24);
        this.line(rectStart, this.BAR_Y_OFF - 12, rectStart, this.BAR_Y_OFF);

        rectStart += rectWidth;
      }
      this.line(rectStart, this.BAR_Y_OFF - 12, rectStart, this.BAR_Y_OFF);

      // draw summary stats on the right
      this.ctx.fillStyle = 'black';
      this.ctx.fillText('MKP', this.BAR_X_OFF + this.BAR_WIDTH + 12, this.BAR_Y_OFF + 30);
      this.ctx.fillText('Weekly Average', this.BAR_X_OFF + this.BAR_WIDTH + 12, this.BAR_Y_OFF + 150);

      this.ctx.fillStyle = barColor;
      this.ctx.font = '80px Roboto';
      if (tweenKp !== undefined && !isNaN(tweenKp))
        this.ctx.fillText(`${tweenKp.toFixed(3)}`, this.BAR_X_OFF + this.BAR_WIDTH + 12, this.BAR_Y_OFF + 100);
      if (this.target) this.ctx.fillText(`${this.target.toFixed(2)}`, this.BAR_X_OFF + this.BAR_WIDTH + 12, this.BAR_Y_OFF + 220);
    }
  }

  /**
   * Handles updates to the KP value. Tweens the bar width from
   * its current length to the new length.
   */
  async _update(kp, oldKp) {
    if (!this.ctx) {
      this.canvas = this.shadowRoot.getElementById('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.ctx.font = '28px Roboto';
      this.ctx.lineWidth = 4;
    }

    if (!kp) kp = 0;
    if (!oldKp || this.teamSwitched) oldKp = 0;

    const startValue = (oldKp / (Math.max(this.target, this.kp) * 1.25)) * this.BAR_WIDTH;
    this.endValue = (kp / (Math.max(this.target, this.kp) * 1.25)) * this.BAR_WIDTH;

    // tween this.progressLength from starting value to ending value
    this.progressLength = startValue;

    while (this.progressLength <= this.endValue) {
      // increment width for smoothing
      this.progressLength += this.progressLength == 0 ? 2 : this.progressLength / 5;

      if (this.progressLength > this.endValue) this.progressLength = this.endValue;

      // sleep 20ms between increments
      await new Promise((r) => setTimeout(r, 20));
    }
    this.teamSwitched = false;
  }
}
window.customElements.define('mkp-chart', MKPChart);

/**
 * KpiWidget is the main widget exported by this file. It handles all
 * of the data querying and management and makes use of the other helper
 * canvas wrapper elements to display the data.
 */
class KpiWidget extends KatapultWidget {
  static get is() {
    return 'kpi-widget';
  }

  static get widgetTitleTemplate() {
    return html` <div>KPI Dashboard</div> `;
  }

  static get headerTemplate() {
    return html`
      <style>
        #header-content {
          float: right;
        }
        #load-icon {
          color: var(--secondary-color);
        }
        paper-spinner-lite {
          --paper-spinner-color: var(--secondary-color);
        }
      </style>
      <span>KPI Dashboard</span>
      <div id="header-content">
        <template is="dom-if" if="[[!loaded]]">
          <material-icon icon="restore" id="load-icon" on-click="loadMKP"></material-icon>
        </template>
        <template is="dom-if" if="[[loading]]">
          <paper-spinner-lite active></paper-spinner-lite>
        </template>
      </div>
    `;
  }

  static get contentTemplate() {
    return html`
      <style>
        .blue {
          color: var(--secondary-color);
          font-weight: bold;
        }
        .grey {
          color: grey;
          font-weight: bold;
        }
        .row {
          display: flex;
        }
        .left {
          flex: 1;
          padding-top: 1em;
        }
        .right {
          flex: 2;
        }
        paper-toggle-button {
          --paper-toggle-button-checked-button-color: var(--secondary-color);
          --paper-toggle-button-checked-bar-color: var(--secondary-color);
        }
      </style>
      <div class="row">
        <div class="left">
          <paper-toggle-button checked="{{funMode}}"> Fun Mode (experimental) </paper-toggle-button>
        </div>
        <div class="right">
          <template is="dom-if" if="[[!funMode]]">
            <p>
              This widget displays your <span class="blue"> current MKP </span> compared to previous weeks. It keeps a record of daily MKP
              totals from the past 8 weeks. Your weekly average is displayed and is used as a rough target for the current week. The
              <span class="grey"> grey bar </span>
              denotes your average MKP by the end of the current weekday.
            </p>
            <p>Press the <span class="blue"> load button </span> in the corner to pull the data and start the chart.</p>
          </template>
          <template is="dom-if" if="[[funMode]]">
            <p>
              This widget displays your <span class="blue"> current MKP </span> compared to previous weeks. One ball in the jar corresponds
              to one MKP. The widget keeps a record of daily MKP totals from the past 8 weeks. Your weekly average is displayed and is used
              as a rough target for the current week (indicated by the dashed line). The <span class="grey"> grey area </span> denotes your
              average MKP by the end of the current weekday.
            </p>
            <p>
              Press the <span class="blue"> load button </span> in the corner to pull the data and start the simulation. If the balls don't
              settle nicely, you can click anywhere to jostle nearby ones a bit.
            </p>
          </template>
        </div>
      </div>
    `;
  }

  static get bodyTemplate() {
    return html`
      <style>
        h3 {
          margin: 2px;
        }
      </style>
      <template is="dom-if" if="[[!funMode]]">
        <h3>Individual</h3>
        <mkp-chart kp="[[kpTotal]]" target="[[target]]" pacer="[[pacer]]" loaded="[[loaded]]"></mkp-chart>
        <paper-row>
          <paper-cell style="max-width: 75px;">
            <h3>Team</h3>
          </paper-cell>
          <template is="dom-if" if="{{userIsAdmin}}" restamp>
            <paper-cell style="max-width: 200px">
              <katapult-drop-down
                id="selectTeam"
                style="text-align: center; max-width: 200px;"
                items="{{teams}}"
                value="[[userTeamName]]"
                label="Select a team"
                on-selected-changed="selectedTeamChanged"
              ></katapult-drop-down>
            </paper-cell>
            <paper-cell>
              <katapult-button iconOnly noBorder on-click="editTeam" id="editTeamsButton" icon="create"></katapult-button>
            </paper-cell>
          </template>
        </paper-row>
        <mkp-chart id="teamChart" kp="[[teamKpTotal]]" target="[[teamTarget]]" pacer="[[teamPacer]]" loaded="[[loaded]]" team></mkp-chart>
      </template>
      <template is="dom-if" if="[[funMode]]">
        <div style="display: flex">
          <div style="flex: 1">
            <h2>Individual</h2>
            <mkp-ball-jar kp="[[kpTotal]]" target="[[target]]" pacer="[[pacer]]" loaded="[[loaded]]"></mkp-ball-jar>
          </div>
          <div style="flex: 1">
            <h2>Team</h2>
            <mkp-ball-jar kp="[[teamKpTotal]]" target="[[teamTarget]]" pacer="[[teamPacer]]" loaded="[[loaded]]" team></mkp-ball-jar>
          </div>
        </div>
      </template>

      <katapult-dialog-legacy id="editTeamsDialog" no-cancel-on-outside-click draggable>
        <span slot="title">Edit Teams</span>
        <div slot="body">
          <paper-row>
            <paper-cell>
              <katapult-drop-down
                id="selectTeamToEdit"
                style="text-align: center; max-width: 200px;"
                items="{{teams}}"
                value="[[selectedTeam]]"
                label="Select a team"
                on-selected-changed="handleTeamChange"
              >
                <paper-item id="deleteTeamButton" on-click="toggleDeleteTeam">Delete Team</paper-item>
                <paper-item id="createTeamButton" on-click="openCreateTeamDialog">Create Team</paper-item>
              </katapult-drop-down>
            </paper-cell>
            <paper-cell>
              <katapult-drop-down
                id="addTeamMember"
                items="{{usernames}}"
                label="Users"
                on-selected-changed="addUserToTeam"
              ></katapult-drop-down>
            </paper-cell>
          </paper-row>
          <template id="team" is="dom-repeat" items="{{userTeam}}">
            <paper-row>
              <paper-cell style="max-width: 175px; padding-left: 75px;">{{item}}</paper-cell>
              <paper-cell>
                <katapult-button iconOnly noBorder id="removeUser" icon="delete" on-click="removeMember"></katapult-button>
              </paper-cell>
            </paper-row>
          </template>
        </div>
        <div slot="buttons">
          <katapult-button on-click="closeEditTeamDialog">Cancel</katapult-button>
          <katapult-button on-click="updateTeam" color="var(--secondary-color)">Update</katapult-button>
        </div>
      </katapult-dialog-legacy>
      <katapult-dialog-legacy id="createTeamDialog">
        <span slot="title">Create New Team</span>
        <div slot="body">
          <paper-row>
            <paper-cell>New Team Name:</paper-cell>
            <paper-cell>Add Users:</paper-cell>
          </paper-row>
          <paper-row>
            <paper-cell><paper-input id="newTeamName" label="Enter a New Team Name"></paper-input></paper-cell>
            <paper-cell>
              <katapult-drop-down
                id="addNewTeamMember"
                items="{{usernames}}"
                label="Users"
                on-selected-changed="addUserToTeam"
              ></katapult-drop-down>
            </paper-cell>
          </paper-row>
          <template id="newTeam" is="dom-repeat" items="{{newTeamUsers}}">
            <paper-row>
              <paper-cell style="max-width: 100px">{{item}}</paper-cell>
              <paper-cell>
                <katapult-button iconOnly noBorder id="removeUser" icon="delete" on-click="removeNewTeamMember"></katapult-button>
              </paper-cell>
            </paper-row>
          </template>
        </div>
        <div slot="buttons">
          <katapult-button on-click="closeCreateTeamDialog">Cancel</katapult-button>
          <katapult-button on-click="createNewTeam" color="var(--secondary-color)">Create</katapult-button>
        </div>
      </katapult-dialog-legacy>
    `;
  }

  static get properties() {
    return {
      funMode: {
        type: Boolean,
        value: false
      },
      kpTotal: {
        type: Number,
        value: 0
      },
      target: {
        type: Number
      },
      pacer: {
        type: Number
      },
      teamKpTotal: {
        type: Number,
        value: 0
      },
      teamTarget: {
        type: Number
      },
      teamPacer: {
        type: Number
      },
      loaded: {
        type: Boolean,
        value: false
      },
      loading: {
        type: Boolean,
        value: false
      },
      paperHeader: {
        type: Boolean,
        value: true
      },
      icon: {
        type: String,
        value: 'keyboard_arrow_down'
      },
      userIsAdmin: {
        type: Boolean,
        value: false,
        // this resets to the proper value stored in the database if someone tries to escalate their privileges - naughty, naughty
        observer: 'isUserAdmin'
      },
      update: {
        type: Object,
        value: {}
      },
      newTeamUpdate: {
        type: Object,
        value: {}
      },
      newTeamUsers: {
        type: Array,
        value: []
      }
    };
  }

  ready() {
    super.ready();
    this.getUsers();
    this.getUserNames();
  }

  /**
   * Gets the corresponding "Friday-noon" DateTime
   * for the given date.
   * @param {DateTime} date date to use
   * @returns {DateTime} the start date
   */
  getStartDate(date) {
    // get friday noon for this week
    let fri = date.set({ weekday: 5 }).minus({
      hours: date.hour - 12,
      minutes: date.minute,
      seconds: date.second,
      milliseconds: date.millisecond
    });

    // if it is in the future, we actually want friday from last week
    if (date < fri) fri = fri.minus({ days: 7 });

    return fri;
  }

  /**
   * Gets the index in the stored week data array corresponding to today.
   * @param {DateTime} date Date to use
   * @returns {number} index
   */
  getWeekIndex(date) {
    switch (date.weekday) {
      case 6: // ignore weekends
      case 7:
        return 0;
      case 5: // Fridays split at noon
        return date.hour >= 12 ? 0 : 5;

      default: // Monday - Thursday
        return date.weekday;
    }
  }

  // gets a lookup table of {username: uid} key-value pairs
  async getUserNames() {
    this.usernameLookup = {};
    if (!this.users) await this.getUsers();
    for (const user of this.users) {
      let email = await FirebaseWorker.ref(`/photoheight/company_space/katapult/users/${user}/email`)
        .once('value')
        .then((s) => s.val());
      this.usernameLookup[this.emailToUsername(email)] = user;
    }
    this.usernames = Object.keys(this.usernameLookup);
  }

  emailToUsername(email) {
    return email.substring(0, email.indexOf('@'));
  }

  async isUserAdmin() {
    //check if the user has admin rights
    let users = await FirebaseWorker.ref(`photoheight/home/widget_catalog/widget_library/9/access/katapult/users`)
      .once('value')
      .then((s) => s.val());
    let isAdmin = users[this.user.uid] == 'admin';
    this.userIsAdmin = isAdmin;
  }

  async editTeam() {
    // sets the selected team to display initially in the edit teams dialog
    this.selectedTeam = this.shadowRoot.querySelector('#selectTeam').value;
    this.shadowRoot.querySelector('#editTeamsDialog').open();
  }

  async addUserToTeam(e) {
    let elem = e.path[0].id;
    let user = SquashNulls(e, 'detail', 'selectedItem');
    let team = null;
    const userPath = `photoheight/home/widget_catalog/widget_library/9/access/katapult/users`;
    let userLookup = await FirebaseWorker.ref(userPath)
      .once('value')
      .then((s) => s.val());
    if (elem == 'addTeamMember') {
      team = this.shadowRoot.querySelector('#selectTeamToEdit').selectedItem;
      // add the user locally and update the "update" object accordingly
      if (team) {
        this.push('userTeam', user);
        user = this.usernameToUid(user);
        this.update[`/${team}/${user}`] = userLookup[user];
      }
    } else {
      team = this.shadowRoot.querySelector('#newTeamName').value;
      if (team) {
        this.push('newTeamUsers', user);
        user = this.usernameToUid(user);
        this.newTeamUpdate[`/${team}/${user}`] = userLookup[user];
      }
    }
  }

  getObjKey(obj, value) {
    return Object.keys(obj).find((key) => obj[key] === value);
  }

  uidsToUsernames(uids) {
    let usernames = [];
    uids.forEach((uid) => {
      usernames.push(this.getObjKey(this.usernameLookup, uid));
    });
    return usernames;
  }

  usernameToUid(username) {
    return this.usernameLookup[username];
  }

  async removeMember(e) {
    let team = this.shadowRoot.querySelector('#selectTeamToEdit').selectedItem;
    // get the user associated with the delete button
    let user = e.model.__data.item;
    //remove the member from the selected team locally
    this.userTeam = this.arrayRemove(this.userTeam, user);
    // get user back to uid reference
    user = this.usernameToUid(user);
    if (team) this.update[`/${team}/${user}`] = null;
  }

  removeNewTeamMember(e) {
    let team = this.shadowRoot.querySelector('#newTeamName').value;
    let user = e.model.__data.item;
    this.newTeamUsers = this.arrayRemove(this.newTeamUsers, user);
    user = this.usernameToUid(user);
    if (team) this.newTeamUpdate[`/${team}/${user}`] = null;
  }

  arrayRemove(arr, value) {
    // remove specified value from the array
    return arr.filter(function (ele) {
      return ele != value;
    });
  }

  async updateTeam() {
    const teamPath = `/photoheight/company_space/katapult/user_data/${this.user.uid}/kpiWidgetTeams`;
    if (this.userIsAdmin) {
      await FirebaseWorker.ref(teamPath).update(this.update);
      this.closeEditTeamDialog();
    }
  }

  closeEditTeamDialog() {
    this.update = {};
    this.shadowRoot.querySelector('#editTeamsDialog').close();
  }

  async getUsers() {
    // get all uids that have access to this widget
    const accessPath = `photoheight/home/widget_catalog/widget_library/9/access/katapult/users`;
    let users = await FirebaseWorker.ref(accessPath)
      .once('value')
      .then((s) => s.val());
    this.users = Object.keys(users);
  }

  /**
   * Gets a list of uids for the user's team members.
   * @returns {Promise<string[]>} a list of uids
   */
  async getTeam(team) {
    //const accessPath = "photoheight/home/widget_catalog/widget_library/9/access/katapult/users";
    const accessPath = `/photoheight/company_space/katapult/user_data/${this.user.uid}/kpiWidgetTeams/${team}`;
    const users = await FirebaseWorker.ref(accessPath)
      .once('value')
      .then((s) => s.val());

    // gets all uids on a team
    let uids = Object.keys(users);
    this.userTeam = this.uidsToUsernames(uids);
    return uids;
  }

  async getUserTeamName(user) {
    const accessPath = `/photoheight/company_space/katapult/user_data/${this.user.uid}/kpiWidgetTeams`;
    let teams = await FirebaseWorker.ref(accessPath)
      .once('value')
      .then((s) => s.val());
    if (!this.teams) this.teams = Object.keys(teams);

    for (let i = 0; i < this.teams.length; i++) {
      if (teams[this.teams[i]].hasOwnProperty(user)) {
        this.userTeamName = this.teams[i];
        return this.userTeamName;
      }
    }
  }

  handleTeamChange(e) {
    if (!this.isDeleteTeam) this.selectedTeamChanged(e, true);
    else this.deleteTeam(e);
  }

  selectedTeamChanged(e, isEditTeamDropdown) {
    let teamMkpChart = this.shadowRoot.querySelector('#teamChart');
    teamMkpChart.teamSwitched = true;
    if (isEditTeamDropdown !== true) this.loadMKP(e);
    else {
      let selectedTeam = SquashNulls(e, 'detail', 'selectedItem');
      this.getTeam(selectedTeam);
    }
  }

  toggleDeleteTeam(e) {
    this.isDeleteTeam = !this.isDeleteTeam;
    if (this.isDeleteTeam) this.shadowRoot.querySelector('#deleteTeamButton').style.backgroundColor = 'red';
    else this.shadowRoot.querySelector('#deleteTeamButton').style.backgroundColor = null;
  }

  deleteTeam(e) {
    let selectedTeam = SquashNulls(e, 'detail', 'selectedItem');
    if (selectedTeam) {
      this.teams = this.arrayRemove(this.teams, selectedTeam);
      this.update[`/${selectedTeam}`] = null;
      // toggle off delete mode
      this.toggleDeleteTeam();
    }
  }

  openCreateTeamDialog() {
    this.shadowRoot.querySelector('#createTeamDialog').open();
  }

  async createNewTeam() {
    let team = this.shadowRoot.querySelector('#newTeamName').value;

    // add new team to team list - this forces dropdown to rerender its items
    let temp = [...this.teams];
    temp.push(team);
    this.teams = temp;

    await FirebaseWorker.ref(`/photoheight/company_space/katapult/user_data/${this.user.uid}/kpiWidgetTeams`).update(this.newTeamUpdate);

    this.closeCreateTeamDialog();
  }

  closeCreateTeamDialog() {
    // clears fields and update array and closes create team dialog
    this.newTeamUpdate = {};
    this.newTeamUsers = [];
    this.shadowRoot.querySelector('#newTeamName').value = null;
    this.shadowRoot.querySelector('#addNewTeamMember').value = null;
    this.shadowRoot.querySelector('#createTeamDialog').close();
  }

  /**
   * Loads the MKP value from the database, updates the user data
   * if necessary, and opens the snapshot listener for future changes
   * to the data.
   */
  async loadMKP(e) {
    const startDate = this.getStartDate(DateTime.now());

    const uid = this.user.uid;

    const actionModelPath = `photoheight/company_space/${this.userGroup}/models/proficiency_report/default`;
    const actionTrackingPath = `companies/${this.userGroup}/action_tracking`;
    const dataPath = `photoheight/company_space/${this.userGroup}/user_data/${uid}/home/kpi_dashboard`;

    const targetHourlyRates = await FirebaseWorker.ref(`photoheight/company_space/${this.userGroup}/project_tracking/target_hourly_rate`)
      .once('value')
      .then((s) => s.val());
    // if team is selected from dropdown, use that team
    // else find what team the user is on
    let selectedTeam = SquashNulls(e, 'detail', 'selectedItem') || (await this.getUserTeamName(this.user.uid));

    const team = await this.getTeam(selectedTeam);

    // get conversions
    const data = await FirebaseWorker.ref(actionModelPath)
      .once('value')
      .then((s) => s.val());

    this.loading = true;
    this.loaded = true;

    // object to look up action KP values
    let actionLookup = {};
    for (const actionType of data) {
      if (!actionType.value) {
        // action has no value differences
        actionLookup[actionType.actionId] = actionType.milestone_value;
      } else {
        // action distinguishes between different values
        if (!actionLookup[actionType.actionId]) actionLookup[actionType.actionId] = {};
        actionLookup[actionType.actionId][actionType.value] = actionType.milestone_value;
      }
    }

    const getKpRate = (date) => {
      let kpRate = targetHourlyRates[0].rate;
      for (let i = targetHourlyRates.length - 1; i >= 0; i--) {
        if (DateTime.fromISO(targetHourlyRates[i].start_date) < DateTime.fromJSDate(date)) {
          kpRate = targetHourlyRates[i].rate;
          break;
        }
      }
      return kpRate;
    };

    // helper function to get the KP value for an action
    const getKP = (a, v, timestamp) => {
      let rate = typeof actionLookup[a] == 'object' ? actionLookup[a][v] : actionLookup[a];
      return rate * getKpRate(timestamp.toDate());
    };
    // helper function to get the running daily totals for a certain
    // number of weeks into the past
    const getWeekTotals = async (numWeeks, _uid) => {
      let weeks = [];

      // get query bounds
      const dateRangeStart = startDate.minus({ days: 7 * numWeeks });
      const dateRangeEnd = startDate;

      // helper function to filter the query results into the desired daily sums
      const getWeekdaySums = (d, start) => {
        const end = start.plus({ days: 7 });
        const week = d.filter((a) => a.timestamp.seconds > start.toSeconds() && a.timestamp.seconds < end.toSeconds());

        let dayOffsets = [
          { hours: 12 }, // Friday noon - end of Friday
          { days: 3 }, // end of Friday - end of Monday
          { days: 1 }, // end of Monday - end of Tuesday
          { days: 1 }, // end of Tuesday - end of Wednesday
          { days: 1 }, // end of Wednesday - end of Thursday
          { hours: 12 } // end of Thursday - Friday noon
        ];
        let dayMarker = start.plus(dayOffsets.shift());
        let weekSums = [];
        weekSums.push(
          week.reverse().reduce((acc, action) => {
            if (action.timestamp.seconds > dayMarker.toSeconds()) {
              weekSums.push(acc);
              dayMarker = dayMarker.plus(dayOffsets.shift());
            }
            return (acc += getKP(action.action_id, action.value, action.timestamp) ?? 0);
          }, 0)
        );

        // pad with zeros if array is not long enough
        while (weekSums.length < 6) weekSums = [0, ...weekSums];

        return weekSums;
      };

      await firebase
        .firestore()
        .collection(actionTrackingPath)
        .orderBy('timestamp', 'desc')
        .where('uid', '==', _uid)
        .where('timestamp', '>', dateRangeStart.toJSDate())
        .where('timestamp', '<', dateRangeEnd.toJSDate())
        .get()
        .then((snap) => {
          const docs = snap.docs.map((d) => d.data());
          for (let i = 1; i <= numWeeks; i++) {
            weeks.push(getWeekdaySums(docs, dateRangeEnd.minus({ days: 7 * i })));
          }
        });

      return weeks;
    };

    const updateUserData = async (_uid) => {
      const dataPath = `photoheight/company_space/${this.userGroup}/user_data/${_uid}/home/kpi_dashboard`;
      const userData = await FirebaseWorker.ref(dataPath)
        .once('value')
        .then((s) => s.val());

      // initialize user data if missing
      if (!userData) {
        const weeks = await getWeekTotals(8, _uid);

        await FirebaseWorker.ref(dataPath).set({
          weeks: weeks,
          best: Math.max(...weeks.flat()),
          week: startDate.toSeconds()
        });
      } else {
        // user has data, let's make sure it's up to date
        const weeksToUpdate = startDate.diff(DateTime.fromSeconds(userData.week), 'days').toObject().days / 7;
        if (weeksToUpdate) {
          let weeks = userData.weeks;

          // get new weeks
          const newWeeks = await getWeekTotals(weeksToUpdate, _uid);
          for (let i = 0; i < weeksToUpdate; i++) weeks.pop();
          weeks.unshift(...newWeeks);

          let best = Math.max(...weeks.flat());
          best = best > userData.best ? best : userData.best;

          let week = startDate.toSeconds();

          await FirebaseWorker.ref(dataPath).set({
            weeks: weeks,
            best: best,
            week: week
          });
        }
      }
    };

    // update user data for all team members
    for (const u of team) updateUserData(u);

    // calculate weekly average and point-in-week average (individual)
    // get updated data
    const weekData = (
      await FirebaseWorker.ref(dataPath)
        .once('value')
        .then((s) => s.val())
    ).weeks;
    const weekAvg = weekData.reduce((acc, week) => acc + week[5], 0) / 8;
    const pointInWeekAvg = weekData.reduce((acc, week) => acc + week[this.getWeekIndex(DateTime.now())], 0) / 8;
    this.pacer = pointInWeekAvg / weekAvg;
    this.target = weekAvg;

    // get team values
    let teamWeekAvg = 0;
    let teamPIWAvg = 0;
    for (const u of team) {
      const dataPath = `photoheight/company_space/${this.userGroup}/user_data/${u}/home/kpi_dashboard`;
      const weekData = (
        await FirebaseWorker.ref(dataPath)
          .once('value')
          .then((s) => s.val())
      ).weeks;
      const weekAvg = weekData.reduce((acc, week) => acc + week[5], 0) / 8;
      const pointInWeekAvg = weekData.reduce((acc, week) => acc + week[this.getWeekIndex(DateTime.now())], 0) / 8;
      teamWeekAvg += weekAvg;
      teamPIWAvg += pointInWeekAvg;
    }
    this.teamPacer = teamPIWAvg / teamWeekAvg;
    this.teamTarget = teamWeekAvg;

    // update kpTotal on query changes
    // get current week live
    firebase
      .firestore()
      .collection(actionTrackingPath)
      .orderBy('timestamp', 'desc')
      .where('uid', '==', uid)
      .where('timestamp', '>', startDate.toJSDate())
      .onSnapshot((snap) => {
        this.kpTotal = snap.docs.reduce((acc, doc) => (acc += getKP(doc.data().action_id, doc.data().value, doc.data().timestamp) ?? 0), 0);
      });

    // update teamKpTotal on query changes
    // get current week live for team data
    firebase
      .firestore()
      .collection(actionTrackingPath)
      .orderBy('timestamp', 'desc')
      .where('uid', 'in', team)
      .where('timestamp', '>', startDate.toJSDate())
      .onSnapshot((snap) => {
        this.teamKpTotal = snap.docs.reduce(
          (acc, doc) => (acc += getKP(doc.data().action_id, doc.data().value, doc.data().timestamp) ?? 0),
          0
        );
      });

    this.loading = false;
  }
}
window.customElements.define('kpi-widget', KpiWidget);
